import type { EChartsOption } from 'echarts';

import type { TimeRange, TimeSeriesChartNavigation } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import { toTimeDomain } from './utils/timeDomain';
import type { TimeDomain } from './utils/timeDomain';

export type ChartUpdateSnapshot = {
  timeDomain: TimeDomain;
  hasNavigation: boolean;
  /** Which series and axes exist, in order — not their data. */
  structure: string;
};

export type ChartUpdatePolicy = {
  /** ECharts components that must drop entries missing from the new option. */
  replaceMerge: string[];
  shouldResetDataZoomRange: boolean;
};

/**
 * Structural components replace-merge so stale series and axes disappear. Data zoom normally
 * stays merged so its user-controlled viewport survives; it is replaced only across a reset boundary.
 */
const REPLACE_MERGE_COMPONENTS = ['series', 'xAxis', 'yAxis', 'grid'];
const REPLACE_MERGE_COMPONENTS_WITH_DATA_ZOOM = [...REPLACE_MERGE_COMPONENTS, 'dataZoom'];

type OptionComponent = { id?: unknown } | null | undefined;

function getComponentIds(components: unknown): string {
  return Array.isArray(components)
    ? components.map((component: OptionComponent) => String(component?.id ?? '')).join(',')
    : '';
}

/**
 * Identifies the option's shape: how many series and axes it has and under which ids. Panning and
 * zooming only swap the data inside the same series, which leaves this unchanged.
 */
export function getOptionStructure(option: EChartsOption): string {
  return [getComponentIds(option.series), getComponentIds(option.xAxis), getComponentIds(option.yAxis)].join('|');
}

export function createChartUpdateSnapshot(
  option: EChartsOption,
  timeDomain: TimeRange,
  navigation: TimeSeriesChartNavigation | undefined
): ChartUpdateSnapshot {
  return {
    timeDomain: toTimeDomain(timeDomain),
    hasNavigation: navigation !== undefined,
    structure: getOptionStructure(option),
  };
}

export function areTimeDomainsEqual(previous: TimeDomain, current: TimeDomain): boolean {
  return previous[0] === current[0] && previous[1] === current[1];
}

/**
 * A new domain or a navigation on/off switch is a reset boundary; everything else keeps
 * the zoom. When only the data inside unchanged series moved — which is what a settled pan or zoom
 * does, since the renderer re-thins each line for the new window — nothing is replaced at all:
 * replacing a series rebuilds it from scratch in ECharts and makes it animate in, which reads as
 * the whole chart flickering on every navigation step.
 */
export function getChartUpdatePolicy(previous: ChartUpdateSnapshot, current: ChartUpdateSnapshot): ChartUpdatePolicy {
  const hasTimeDomainChanged = !areTimeDomainsEqual(previous.timeDomain, current.timeDomain);
  const hasNavigationPresenceChanged = previous.hasNavigation !== current.hasNavigation;
  const isResetBoundary = hasTimeDomainChanged || hasNavigationPresenceChanged;
  const hasStructureChanged = previous.structure !== current.structure;

  if (!isResetBoundary && !hasStructureChanged) {
    return { replaceMerge: [], shouldResetDataZoomRange: false };
  }

  return {
    replaceMerge: isResetBoundary ? REPLACE_MERGE_COMPONENTS_WITH_DATA_ZOOM : REPLACE_MERGE_COMPONENTS,
    shouldResetDataZoomRange: current.hasNavigation && isResetBoundary,
  };
}

export function resetDataZoomRange(option: EChartsOption): EChartsOption {
  if (!Array.isArray(option.dataZoom)) {
    return option;
  }

  return {
    ...option,
    dataZoom: option.dataZoom.map((dataZoom) => ({
      ...dataZoom,
      start: 0,
      end: 100,
    })),
  };
}

/** When each line series first reached the chart, by series id. */
export type LineAppearances = ReadonlyMap<string, number>;

export function getLineSeriesIds(option: EChartsOption): string[] {
  if (!Array.isArray(option.series)) {
    return [];
  }

  return option.series.flatMap((series) =>
    series.type === 'line' && series.id !== undefined ? [String(series.id)] : []
  );
}

/** Keeps the time of lines already on the chart, starts new ones at `now` and forgets removed ones. */
export function updateLineAppearances(previous: LineAppearances, lineIds: string[], now: number): LineAppearances {
  return new Map(lineIds.map((id) => [id, previous.get(id) ?? now]));
}

/** Lines whose entry animation has run its course; switching animation off any earlier would cut it short. */
export function getSettledLineIds(appearances: LineAppearances, now: number, entryDurationMs: number): Set<string> {
  return new Set([...appearances].filter(([, appearedAt]) => now - appearedAt >= entryDurationMs).map(([id]) => id));
}

/**
 * Settled lines update with animation off. Updates never move visibly anyway (the update duration is
 * zero), but a line allowed to animate is diffed point by point on every update and every zoom and
 * pan step. A new line keeps animation on, so it still enters the way it always did.
 */
export function withoutLineUpdateAnimation(option: EChartsOption, settledLineIds: ReadonlySet<string>): EChartsOption {
  if (!Array.isArray(option.series)) {
    return option;
  }

  return {
    ...option,
    series: option.series.map((series) =>
      series.type === 'line' && settledLineIds.has(String(series.id)) ? { ...series, animation: false } : series
    ),
  };
}
